import { Injectable } from '@angular/core';
import { AlertController, LoadingController } from '@ionic/angular';
import { APIService } from './api.service';
import { AuthService } from './auth.service';
import { ObserverService } from './observer.service';
import { Router } from '@angular/router';

@Injectable()
export class OrderService {

    spinner = null;

    constructor(
        public alertController: AlertController,
        public loadingController: LoadingController,
        private router: Router,
        private authService: AuthService,
        private apiService: APIService,
        private observerService: ObserverService,
    ) { }

    addOrderComment(order_id, comment) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.addOrderComment(order_id, comment).then((comment) => {
                    if (comment) {
                        this.observerService.publish('order_comment_added',{order_id,comment});
                    }
                    resolve()
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to add the comment.");
                    } 
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to add a comment.");
                    } 
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to add a comment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    addOrderFulfillment(order_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.addOrderFulfillment(order_id, data).then((fulfillment) => {
                    if(fulfillment) {
                        this.getOrder(order_id).then(order => {
                            this.observerService.publish('order_updated', order);
                        });
                        resolve();
                    }
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to add this fulfillment.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to add a fulfillment.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to add a fulfillment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    addOrderPayment(order_id, data){
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.addOrderPayment(order_id, data).then(() => {
                    this.getOrder(order_id).then(order=>{
                        this.observerService.publish('order_updated', order);
                    });
                    resolve();
                })
                .catch((error) => {
                    if(error.status == 400) {
                        if(error.error.errors.length != 0){
                            for(let i = 0; i < error.error.errors.length; i++) {
                                let error_message = error.error.errors[i];
                                if(error_message == "gift_voucher_insufficient_remaining_balance") {
                                    this.presentAlert('Problem','Insufficent balance', 'The gift voucher doesn\'t have enough balance left.');
                                    break;
                                }
                                else if(error_message == "gift_voucher_expired") {
                                    this.presentAlert('Problem','Expired', 'The gift voucher has expired.');
                                    break;
                                }
                                else if(error_message == "gift_voucher_incorrect_currency") {
                                    this.presentAlert('Problem','Incorrect currency', 'The gift voucher is for a different currency.');
                                    break;
                                }
                                else if(error_message == "gift_voucher_originating_order_not_paid") {
                                    this.presentAlert('Problem','Unpaid', 'The gift voucher can\'t be used yet as it hasn\'t been paid for.');
                                    break;
                                }
                                else if(error_message == "person_store_credit_insufficient_balance" || error_message == "organisation_store_credit_insufficient_balance") {
                                    this.presentAlert('Problem', 'Insufficent balance', 'There\'s not enough store credit left.');
                                    break;
                                }
                                else {
                                    this.presentAlert('Problem','Unknown error', 'Unfortunately something went wrong when trying to add the payment.');
                                    break;
                                }
                            }
                        }
                        else {
                            this.presentAlert('Problem','Unknown error', 'Unfortunately something went wrong when trying to add the payment.');
                        }
                    }
                    else if(error.status == 403) {
                        this.presentAlert('Problem','Insufficient permissions', 'You don\'t have permission to add a payment.');
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert('Problem','Unavailable', 'Unfortunately we\'re unable to add a payment at this time.');
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    addOrderReturn(order_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.addOrderReturn(order_id, data).then((order_return) => {
                    if(order_return) {
                        this.getOrder(order_id).then(order => {
                            this.observerService.publish('order_updated', order);
                        });
                        resolve();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status == 400) {
                        this.presentAlert('Problem','Unknown error', 'Unfortunately something went wrong when trying to add this return.');
                    } 
                    else if(error.status == 403) {
                        this.presentAlert('Problem','Insufficient permissions', 'You don\'t have permission to add a return.');
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert('Problem','Unavailable', 'Unfortunately we\'re unable to add a return at this time.');
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    addOrderRefund(order_id, data) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.addOrderRefund(order_id, data).then((refund) => {
                    if(refund) {
                        this.getOrder(order_id).then(order => {
                            this.observerService.publish('order_updated', order);
                        });
                        resolve(refund);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status == 400) {
                        this.presentAlert('Problem','Unknown error', 'Unfortunately something went wrong when trying to add this refund.');
                    } 
                    else if(error.status == 403) {
                        this.presentAlert('Problem','Insufficient permissions', 'You don\'t have permission to add a refund.');
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert('Problem','Unavailable', 'Unfortunately we\'re unable to add a refund at this time.');
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    addOrderRefundPayment(order_id, refund_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.addOrderRefundPayment(order_id, refund_id, data).then((payment) => {
                    if(payment) {
                        this.getOrderRefund(order_id, refund_id).then((refund) => {
                            if(refund) {
                                this.observerService.publish("order_refund_updated", refund);
                                this.getOrder(order_id).then(order=>{
                                    this.observerService.publish('order_updated', order);
                                });
                            }
                        });
                        resolve();
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status == 400) {
                        this.presentAlert('Problem','Unknown error', 'Unfortunately something went wrong when trying to the order refund payment.');
                    } 
                    else if(error.status == 403) {
                        this.presentAlert('Problem','Insufficient permissions', 'You don\'t have permission to add an order refund payment.');
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert('Problem','Unavailable', 'Unfortunately we\'re unable to add an order refund at this time.');
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrder(order_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrder(order_id).then((order) => {
                    if (order) {
                        resolve(order);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrders(params) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrders(params).then((orders) => {
                    if(orders) {
                        resolve(orders);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the orders.");
                    } 
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view orders.");
                    } 
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve orders at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderFulfillment(order_id, fulfillment_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderFulfillment(order_id, fulfillment_id).then((fulfillment) => {
                    if(fulfillment) {
                        resolve(fulfillment);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order fulfillment.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order fulfillment.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order fulfillment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderItem(order_id, item_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderItem(order_id, item_id).then((item) => {
                    if(item) {
                        resolve(item);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order item.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order item.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order item at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderPayment(order_id, payment_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderPayment(order_id, payment_id).then((payment) => {
                    if(payment) {
                        resolve(payment);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order payment.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order payment.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order payment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderRefund(order_id, refund_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderRefund(order_id, refund_id).then((refund) => {
                    if(refund) {
                        resolve(refund);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order refund.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order refund.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order refund at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderRefundPayment(order_id, refund_id, payment_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderRefundPayment(order_id, refund_id, payment_id).then((payment) => {
                    if(payment) {
                        resolve(payment);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order refund payment.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order refund payment.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order refund payment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderReturn(order_id, return_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderReturn(order_id, return_id).then((order_return) => {
                    if(order_return) {
                        resolve(order_return);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order return.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order return.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order return at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderInvoice(order_id, format, printer_paper_size) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderInvoice(order_id, format, printer_paper_size).then((order_invoice) => {
                    resolve(order_invoice)
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve an invoice.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to get an invoice.");
                    }
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve an invoice at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderXeroInvoice(order_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderXeroInvoice(order_id).then((xero_invoice) => {
                    resolve(xero_invoice)
                })
                .catch((error) => {
                    if(error.status == 400) {
                        if(error.error.errors.length != 0){
                            for(let i = 0; i < error.error.errors.length; i++) {
                                let error_message = error.error.errors[i];
                                if(error_message == "xero_not_connected") {
                                    this.presentAlert("Problem","Xero not connected", "Xero must be connected to retrieve the Xero invoice.");
                                    break;
                                }
                                else if(error_message == "xero_disconnected") {
                                    this.presentAlert("Problem","Xero has been disconnected.", "Xero must be connected to retrieve the Xero invoice.");
                                    break;
                                }
                                else if(error_message == "xero_unavailable") {
                                    this.presentAlert("Problem","Xero is unavailable.", "Xero must be available to retrieve the Xero invoice.");
                                    break;
                                }
                                else if(error_message == "xero_invoice_not_found") {
                                    this.presentAlert("Problem","Xero invoice not found.", "Xero was unable to retrieve the Xero invoice.");
                                    break;
                                }
                                else {
                                    this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the Xero invoice.");
                                    break;
                                }
                            }
                        }
                        else {
                            this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the Xero invoice.");
                        }

                    }
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view a Xero invoice.");
                    }
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the Xero invoice at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderPackingSlip(order_id, format, items?) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                if(items && items.length) {
                    this.apiService.getOrderPackingSlip(order_id, format, items).then((packing_slip) => {
                        resolve(packing_slip)
                    })
                    .catch((error) => {
                        if(error.status == 400) {
                            this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the packing slip.");
                        }
                        else if(error.status == 403) {
                            this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view a packing slip.");
                        }
                        else if(error.status == 404 || error.status == 0) {
                            this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the packing slip at this time.");
                        }
                        reject();
                    });
                }
                else {
                    this.apiService.getOrderPackingSlip(order_id, format).then((packing_slip) => {
                        resolve(packing_slip)
                    })
                    .catch((error) => {
                        if(error.status == 400) {
                            this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the packing slip.");
                        }
                        else if(error.status == 403) {
                            this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view a packing slip.");
                        }
                        else if(error.status == 404 || error.status == 0) {
                            this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the packing slip at this time.");
                        }
                        reject();
                    });
                }
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderFulfillmentPackingSlip(order_id, fulfillment_id, format) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderFulfillmentPackingSlip(order_id, fulfillment_id, format).then((packing_slip) => {
                    resolve(packing_slip)
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the packing slip.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view a packing slip.");
                    }
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the packing slip at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    // NOTE: currently unused
    // getOrderAddressLabel(order_id, format) {
    //     return new Promise((resolve, reject) => {
    //         this.authService.isLoggedIn().then(() => {
    //             this.apiService.getOrderAddressLabel(order_id, format).then((address_label) => {
    //                 resolve(address_label)
    //             })
    //             .catch((error) => {
    //                 if(error.status == 400) {
    //                     this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the address label.");
    //                 }
    //                 else if(error.status == 403) {
    //                     this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view an address label.");
    //                 }
    //                 else if(error.status == 404 || error.status == 0) {
    //                     this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the address label at this time.");
    //                 }
    //                 reject();
    //             });
    //         })
    //         .catch(() => {
    //             this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
    //                 window.location.reload();
    //             });
    //         });
    //     });
    // }

    getOrderDeliveryNote(order_id, format) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderDeliveryNote(order_id, format).then((delivery_note) => {
                    resolve(delivery_note)
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the delivery note.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view a delivery note.");
                    }
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the delivery note at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderCollectionNote(order_id, format) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderCollectionNote(order_id, format).then((collection_note) => {
                    resolve(collection_note)
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong while trying to retrieve the collection note.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view a collection note.");
                    }
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the collection note at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderComments(order_id) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrder(order_id).then((order: any) => {
                    let comments = [];
                    if(order && order.comments) {
                        comments = order.comments;
                    }
                    resolve(comments);
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get the order comments.");
                    } 
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view the order comments.");
                    } 
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to retrieve the order comments at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderTickets(order_id, format, printer_paper_size) {
        return new Promise((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.getOrderTickets(order_id, format, printer_paper_size).then((order_tickets) => {
                    resolve(order_tickets)
                })
                .catch((error) => {
                    if(error.status == 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get tickets.");
                    }
                    else if(error.status == 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to get tickets.");
                    }
                    else if(error.status == 404 || error.status == 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to get tickets at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    getOrderTicket(ticket_id, format, printer_paper_size) {
        return new Promise((resolve, reject) => {
            this.authService
            .isLoggedIn()
            .then(() => {
                this.apiService
                    .getTicket(ticket_id, format, printer_paper_size)
                    .then((order_ticket) => {
                        resolve(order_ticket)
                    })
                    .catch((error) => {
                        console.error(error);
                        if(error.status === 400) {
                            this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get a ticket.");
                        } else if(error.status === 403) {
                            this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to get a ticket.");
                        } else if(error.status === 404 || error.status === 0) {
                            this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to get a ticket at this time.");
                        }
                        reject();
                    });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    sendOrderConfirmationEmail(order_id, email_address) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.sendOrderConfirmationEmail(order_id, email_address).then(() => {
                    resolve()
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to send the order confirmation email.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to send an order confirmation email.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to send the order confirmation email at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }           

    sendOrderInvoiceEmail(order_id, email_address) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.sendOrderInvoiceEmail(order_id, email_address).then(() => {
                    resolve()
                })
                .catch((error) => {
                    console.error(error);
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to send the order invoice email.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to send an order invoice email.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to send the order invoice email at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }        

    updateOrder(order_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateOrder(order_id, data).then((order) => {
                    this.observerService.publish("order_updated", order);
                    resolve()
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to update the order.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to update an order.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to update the order at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateOrderFulfillment(order_id, fulfillment_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateOrderFulfillment(order_id, fulfillment_id, data).then((fulfillment) => {
                    this.observerService.publish("order_fulfillment_updated", fulfillment);
                    resolve()
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to update the order fulfillment.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to update an order fulfillment.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to update the order fulfillment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateOrderItem(order_id, item_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateOrderItem(order_id, item_id, data).then((item) => {
                    this.observerService.publish("order_item_updated", item);
                    resolve()
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to update the order item.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to update an order item.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to update the order item at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateOrderPayment(order_id, payment_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateOrderPayment(order_id, payment_id, data).then((payment) => {
                    this.observerService.publish("order_payment_updated", payment);
                    resolve()
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to update the order payment.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to update an order payment.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to update the order payment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateOrderRefund(order_id, refund_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateOrderRefund(order_id, refund_id, data).then((refund) => {
                    this.observerService.publish("order_refund_updated", refund);
                    resolve()
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to update the order refund.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to update an order refund.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to update the order refund at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateOrderRefundPayment(order_id, refund_id, payment_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateOrderRefundPayment(order_id, refund_id, payment_id, data).then((payment) => {
                    this.observerService.publish("order_refund_payment_updated", payment);
                    resolve()
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to update the order refund payment.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to update an order refund payment.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to update the order refund payment at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    updateOrderReturn(order_id, return_id, data) {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.apiService.updateOrderReturn(order_id, return_id, data).then((order_return) => {
                    this.observerService.publish("order_return_updated", order_return);
                    resolve()
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to update the order return.");
                    }
                    else if(error.status === 403) {
                        this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to update an order return.");
                    }
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to update the order return at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    async presentAlert(header, sub_header, message) {
        const alert = await this.alertController.create({
            header: header,
            subHeader: sub_header,
            message: message,
            buttons: ['OK'],
        });
        await alert.present();
    }

}
